import { useEffect, useState, type ReactNode } from "react";

/**
 * Mounts children only after the browser is idle (or after a small timeout
 * fallback). Used to defer non-critical UI off the main-thread budget that
 * the initial render competes for — improving TBT and LCP on mobile.
 */
export function DeferredIdle({
  children,
  timeout = 2000,
}: {
  children: ReactNode;
  timeout?: number;
}) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const trigger = () => {
      if (!cancelled) setReady(true);
    };

    const w = window as Window &
      typeof globalThis & {
        requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
        cancelIdleCallback?: (id: number) => void;
      };

    if (typeof w.requestIdleCallback === "function") {
      const id = w.requestIdleCallback(trigger, { timeout });
      return () => {
        cancelled = true;
        w.cancelIdleCallback?.(id);
      };
    }

    const t = window.setTimeout(trigger, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [timeout]);

  if (!ready) return null;
  return <>{children}</>;
}
